import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Injector, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ApplicationContext, Borrower, Configuration, LoanApplication, LoanStatus, StandardLoanStage, SubStatus } from 'src/app/models';
import { TpoConfiguration, TpoCreditPermissions } from 'src/app/modules/admin/tpo-config/models/tpo-configuration.model';
import { AppDetailsService } from 'src/app/modules/app-details/services/app-details.service';
import { LoanSummaryBarOptions } from 'src/app/modules/loan-summary-bar/components/loan-summary-bar.component';
import { Constants } from 'src/app/services/constants';
import { LoanServicesService } from 'src/app/services/loan/loan-services.service';
import { LoanService } from 'src/app/services/loan/loan.service';
import { MenuService } from 'src/app/services/menu.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import { PizzaTrackerStep } from 'src/app/shared/components/pizza-tracker/pizza-tracker.component';
import { MenuItemStatus } from '../../models/enums/menu-item-status.enum';
import { CreateEscalationDialogComponent } from '../create-escalation-dialog/create-escalation-dialog.component';
import { LoanSummary } from '../loan-summary-card/loan-summary.model';
import { UrlaMortgage } from 'src/app/modules/urla/models/urla-mortgage.model';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { LoanPurposeTypeEnum } from 'src/app/modules/app-details/components/title-history/models/title-order.model';
import { LoanPurpose, TransactionType } from 'src/app/models/config/loan-purpose.model';
import { MortgageService } from 'src/app/services/mortgage.service';
import { Location } from '@angular/common';
import { ExternalCompanyService } from 'src/app/modules/admin/external-company-management/services';
import { KeyDateType } from 'src/app/models/key-dates/key-date-types.enum';

declare const CobrowseIO: any;

@Component({
  selector: 'tpo-app-details',
  templateUrl: './tpo-app-details.component.html',
  styleUrls: ['./tpo-app-details.component.scss'],
  standalone: false
})
export class TpoAppDetailsComponent extends ApplicationContextBoundComponent implements OnInit, AfterViewInit, OnDestroy {

  loanId: number;
  companyId: number;

  loanSummary: LoanSummary;

  isLoadingApplicationDetails: boolean = false;

  trackerSteps: PizzaTrackerStep[] = [];
  activeTrackerStepOrder: number;
  loanSubmissionCompleted: boolean = true;

  isUrlaReadOnly: boolean = false;
  isPRMG: boolean = false;
  isGenway: boolean = false;
  isCompanyRoundHouse: boolean = false;
  useV2Menu: boolean = false;
  isUrlEndsWithLoanId: boolean = false;
  isCompanyPulseProcessing: boolean = false;
  tpoSubmitted: boolean = false;
  isDisableFees: boolean = false;
  isReverseMortgage: boolean = false;
  disclosuresEnabled: boolean = true;

  protected application: LoanApplication;
  protected mortgage: UrlaMortgage;
  protected loanStatus: LoanStatus;
  protected subStatus: SubStatus;
  protected loanPurpose: LoanPurpose;

  protected isPizzaTrackerEnabled: boolean = false;
  protected isBrokerDisclosedAndSubmitted: boolean = false;
  protected showExplanationSectionForMenuStatusIcons: boolean = false;

  private _loanPurposes: LoanPurpose[];
  private _loanStatuses: LoanStatus[];
  private _tpoConfig: TpoConfiguration;

  private _subscription: Subscription;
  private _routeSubscription1: Subscription;
  private _routeSubscription2: Subscription;
  private _loanInfoLoadErrorSubscription: Subscription;
  private _menuItemStatusSubscription: Subscription;
  private _menuItemClickedSubscription: Subscription;
  private _urlaStatusSubscription: Subscription;
  private _applicationStatusTrackingChangesSubscription: Subscription;
  private _routerEventsSubscription: Subscription;

  private disabledTpoAppMenuItems: readonly string[] = [];

  private _isCompanySmartFi: boolean = false;

  protected isMobileLoanSummaryPanelVisible: boolean = false;

  protected loanSummaryOptions: LoanSummaryBarOptions = {
    showCharacteristicsSummary: false,
    showLosLdeSummary: false,
    showStatusSummary: false
  }

  protected stepStatuses: any = {
    urla: MenuItemStatus.Pending,
    services: MenuItemStatus.None,
    pricing: MenuItemStatus.None,
    fees: MenuItemStatus.None,
    disclosure: MenuItemStatus.None,
    submission: MenuItemStatus.None
  }

  protected permissions: TpoFeaturePermissions = new TpoFeaturePermissions();

  constructor(
    private readonly injector: Injector,
    private readonly _menuService: MenuService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _modalService: NgbModal,
    private readonly _notificationService: NotificationService,
    private readonly _router: Router,
    private readonly _loanService: LoanService,
    private readonly _mortgageService: MortgageService,
    private readonly _loanServicesService: LoanServicesService,
    private readonly _appService: AppDetailsService,
    private readonly _externalCompanyService: ExternalCompanyService,
    private readonly _location: Location,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super(injector);

    this.isPRMG = this.applicationContext.isCompanyPRMG;
    this.isGenway = this.applicationContext.isCompanyGenway;
    this.isCompanyRoundHouse = this.applicationContext.isCompanyRoundHouse;
    this.useV2Menu = this.applicationContext.tpoConfig.useV2Menu;
    this.isCompanyPulseProcessing = this.applicationContext.isCompanyPulseProcessing;
    this.initializeDataForMenuAndSummary(this.applicationContext);

    this.isLoadingApplicationDetails = true;
    this._subscription = this.applicationContextService.loanInfoChanges.subscribe(({ context }) => {
      const statusItem = context.tpoMenuStatuses ? context.tpoMenuStatuses[Constants.menu.servicesMenuItems.submission] : null;
      this.tpoSubmitted = statusItem && statusItem.toLowerCase() == MenuItemStatus.Success;
      if (this.isUrlEndsWithLoanId) {
        this.isUrlEndsWithLoanId = false;
        if (this.isCompanyPulseProcessing && !this.tpoSubmitted) {
          this._router.navigateByUrl('tpo/app-details/' + this.loanId + '/urla');
        } else {
          if (this.isCompanyPulseProcessing) {
            this._router.navigateByUrl('tpo/app-details/' + this.loanId + '/tpo-loan-summary');
          } else {
            this._router.navigateByUrl('tpo/app-details/' + this.loanId + '/loan-summary');
          }
        }
      }

      this.initializeDataForMenuAndSummary(context);
    });

    this._appService.getLoanHiddenFields().subscribe((response) => {
      const hiddenFields = this.getHiddenFields(response);

      this.permissions.isSubjectPropertyAddressHidden = hiddenFields.findIndex(f => f === "Subject Property") > -1;
      this.permissions.isAppraisedValueHidden = hiddenFields.findIndex(f => f === "Appraised Value") > -1;
    });

    this._loanInfoLoadErrorSubscription = this.applicationContextService.loanInfoLoadError.subscribe(error => {
      this._notificationService.showError(error, "Access Error!");
      setTimeout(() => {
        this._router.navigateByUrl('/tpo/dashboard');
      }, 2000)
    });

    this._urlaStatusSubscription = this._menuService.urlaStatusChanged.subscribe(status => {
      const step = this.trackerSteps.find(step => step.id === 'urla');
      if (step && status) {
        step.status = status;
      }
    });

    this._applicationStatusTrackingChangesSubscription = this.applicationContextService.applicationTrackingStatusesChangedEvent.subscribe((statuses) => {
      this.updateMenuItemAndPizzaTrackerStatuses(statuses);
    });

    this._routerEventsSubscription = this._router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        if (!this.isUrlEndsWithLoanId) {
          this.isUrlEndsWithLoanId = this._router.url.endsWith(this.loanId?.toString()) || this._router.url.endsWith(this.loanId?.toString() + '/');
          if (this.isUrlEndsWithLoanId) {
            this.isUrlEndsWithLoanId = false;
            this._location.back();
          }
        }

        this.setActiveTrackerStepOrder();
        window.scrollTo(0, 0);
      }
    });
  }

  ngOnInit(): void {
    this._routeSubscription1 = this._activatedRoute.queryParams.subscribe(queryParams => {
      this._routeSubscription2 = this._activatedRoute.params.subscribe(routeParams => {
        const loanId = Number(this._activatedRoute.snapshot.paramMap.get('id'));
        this.isUrlEndsWithLoanId = this._router.url.endsWith(loanId.toString()) || this._router.url.endsWith(loanId.toString() + '/');
        if (loanId && !isNaN(loanId) && loanId != this.loanId) {
          this.loanId = loanId;
          this.isLoadingApplicationDetails = true;
          this.loanSummary = null;
          this.applicationContextService.updateLoanInfo(this.loanId).subscribe(result => {
            this.isLoadingApplicationDetails = false;
          });
        } else {
          this.isLoadingApplicationDetails = false;
        }
      }, error => {
        this.isLoadingApplicationDetails = false;
        this._notificationService.showError(
          error ? error.message : 'Unable update loan info.',
          'Error!'
        );
      });
    });

    this.getPermissions();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    if (this._routeSubscription1) {
      this._routeSubscription1.unsubscribe();
    }
    if (this._routeSubscription2) {
      this._routeSubscription2.unsubscribe();
    }
    if (this._loanInfoLoadErrorSubscription) {
      this._loanInfoLoadErrorSubscription.unsubscribe();
    }
    if (this._menuItemStatusSubscription) {
      this._menuItemStatusSubscription.unsubscribe();
    }
    if (this._menuItemClickedSubscription) {
      this._menuItemClickedSubscription.unsubscribe();
    }
    if (this._urlaStatusSubscription) {
      this._urlaStatusSubscription.unsubscribe();
    }
    if (this._routerEventsSubscription) {
      this._routerEventsSubscription.unsubscribe();
    }
    if (this._applicationStatusTrackingChangesSubscription) {
      this._applicationStatusTrackingChangesSubscription.unsubscribe();
    }
  }

  onMobileLoanSummaryPanelToggleRequested = () => {
    if (window.innerWidth > 1429) {
      return;
    }
    this.isMobileLoanSummaryPanelVisible = !this.isMobileLoanSummaryPanelVisible;
    this._cdr.markForCheck();
  }

  onMobileLoanSummaryPanelClosed = () => {
    this.isMobileLoanSummaryPanelVisible = false;
    this._cdr.markForCheck();
  }

  @HostListener('window:resize')
  onWindowResize = () => {
    if (window.innerWidth > 1429 && this.isMobileLoanSummaryPanelVisible) {
      this.isMobileLoanSummaryPanelVisible = false;
      this._cdr.markForCheck();
    }
  }

  createEscalationDialogModal = () => {
    this._modalService.open(CreateEscalationDialogComponent, Constants.modalOptions.xlarge);
  }

  private initializeDataForMenuAndSummary = (context: ApplicationContext) => {
    if (!context.borrowers || !context.application || !context.currentMortgage) {
      return;
    }

    this._isCompanySmartFi = context.isCompanySmartfi;

    const borrower = context.borrowers[0];
    this.application = context.application;
    this.mortgage = context.currentMortgage;
    this.loanId = this.application.applicationId;
    this.initializeCobrowse(this.loanId);
    this.loanSummary = new LoanSummary();
    this.loanSummary.borrowerName = this.getLlcAwareBorrowerName(context, borrower);
    this.loanSummary.city = context.application.mailingCity;
    this.loanSummary.streetAddress = context.application.mailingStreet;
    this.loanSummary.channel = context.application.channel;
    this.loanSummary.loanNumber = context.application.refNumber;
    this.companyId = context.userPermissions.companyId;

    this._loanPurposes = context.globalConfig.loanPurpose;

    this.permissions.pricingEnabled = context.userPermissions.pricingEnabled;
    this.permissions.isFeesEnabled = context.userPermissions.feesEnabled;
    this.permissions.isAmortizationTableMenuItemVisible = !!context.application?.productPricing?.rate;
    this.permissions.isMersEnabled = context.userPermissions?.mersEnabled;
    this.permissions.isRequiredLoanAndCaseNumbers = context.application.channel !== 'Wholesale' || !context.isTpo;
    this.permissions.isDisclosureGenerationAllowed = context.tpoConfig.isDisclosureGenerationAllowed;
    this.permissions.isLinkEnabled = context.tpoConfig.linkIntegrationEnabled;
    this.permissions.isLinkCalculatorEnabled = context.tpoConfig.linkIncomeCalculatorEnabled;
    this.permissions.isIncomeAnalysisEnabled = this.isIncomeAnalysisEnabledForChannel(context.tpoConfig.incomeAnalysisEnabledChannels, context.application?.channel);

    this.isBrokerDisclosedAndSubmitted = context.application?.disclosurePath == "Broker";
    this.isDisableFees = context.tpoConfig.disableFees;
    if (!context.isCompanyPulseProcessing && ((this.application.channel == 'Wholesale' && context.tpoConfig.wholesalePizzaTrackerEnabled) ||
      (this.application.channel == 'Correspondent' && context.tpoConfig.corrPizzaTrackerEnabled) ||
      (this.application.channel == 'NonDelegatedCorrespondent' && context.tpoConfig.nonDelPizzaTrackerEnabled))) {
      this.isPizzaTrackerEnabled = true;
    }
    this.loanStatus = context.globalConfig.loanStatus.find(s => s.loanStatusId == context.application.loanStatusId);
    if (this.loanStatus) {
      this.loanSummary.loanStatus = this.loanStatus.loanStatusName;
    }
    this.subStatus = context.globalConfig.subStatus.find(s => s.subStatusId == context.application.subStatusId && s.showOnTpoPortal);
    if (this.subStatus) {
      this.loanSummary.subStatusName = this.subStatus.subStatusName;
    }

    this.loanPurpose = this._loanPurposes.find(s => s.loanPurposeId == context.application.loanPurposeId);
    if (this.loanPurpose) {
      this.loanSummary.loanStatusAlias = this.loanStatus?.tpoStatusAlias ?? null;
    }

    if (context.application?.externalCompanyId) {
      this._externalCompanyService.getTpoApplicationExternalCompany(context.application.applicationId).subscribe((company) => {
        if (company) {
          this.disclosuresEnabled = company.disclosuresEnabled;
        }
      });
    }

    this.isReverseMortgage =
      context.globalConfig.loanPurpose.find(
        (x) =>
          x.loanPurposeId == context.application?.loanPurposeId
      )?.transactionType == TransactionType.ReverseMortgage;

    this.isLoadingApplicationDetails = false;
    this.checkLoanPurpose();

    if (context.application?.externalCompanyId) {
      this._externalCompanyService.getTpoApplicationExternalCompany(context.application.applicationId).subscribe((company) => {
        if (company) {
          this.disclosuresEnabled = company.disclosuresEnabled;
          this.populateTrackerSteps(context);
        }
      });
    }
    else {
      this.populateTrackerSteps(context);
    }
  }

  private initializeCobrowse = (applicationId: number) => {
    const authDataJson = localStorage.getItem(Constants.authorization.authorizationDataKey);
    if (authDataJson) {
      const authData = JSON.parse(authDataJson);
      if (authData) {
        try {
          CobrowseIO.customData = {
            user_id: authData.userId,
            user_name: authData.userName,
            user_email: authData.userName,
            device_id: applicationId,
            device_name: authData.userName,
            application_id: applicationId
          };
        } catch (error) { }
      }
    }
  }

  /**
   * Returns the entity name for Entity/LLC borrowers, or the standard display name otherwise.
   * Entity name lives on the mortgage's borrowing entity (URLA Borrower Info), not the
   * per-borrower DocPrep-only entityName field.
   */
  private getLlcAwareBorrowerName = (context: ApplicationContext, borrower: Borrower): string => {
    const mortgageBorrower = context.currentMortgage?.borrowers?.find(
      (mb) => mb.contactId === borrower.borrowerId
    );
    const entityName = context.currentMortgage?.extension?.borrowingEntity?.entityName;
    if (mortgageBorrower?.mortgagePartyType === 'Entity' && entityName) {
      return entityName;
    }
    return borrower.firstName + " " + borrower.lastName;
  }

  private populateTrackerSteps = (applicationContext: ApplicationContext) => {
    const keyDates = applicationContext.applicationKeyDatesByType;
    this.disabledTpoAppMenuItems = applicationContext.userPermissions.disabledTPOAppMenuItems;

    if (keyDates?.[KeyDateType.LEIssued]?.eventDate && !applicationContext.tpoConfig?.allowUrlaEditsPostSubmission) {
      this.isUrlaReadOnly = true;
    }

    this.updateMenuItemAndPizzaTrackerStatuses(applicationContext.tpoMenuStatuses);
  }

  private updateMenuItemAndPizzaTrackerStatuses = (statuses: any) => {
    if (!statuses) {
      return;
    }
    const statusItem = statuses[Constants.menu.servicesMenuItems.submission];
    const tpoSubmitted = statusItem && statusItem.toLowerCase() == MenuItemStatus.Success;
    if (tpoSubmitted && !this.applicationContext?.tpoConfig?.allowUrlaEditsPostSubmission) {
      this.isUrlaReadOnly = true;
    }
    this.populateInitialMenuItemStatuses(statuses);
    if (this.isPizzaTrackerEnabled) {
      this.populateInitialPizzaTrackerStatuses(statuses);
    }
  }

  private populateInitialMenuItemStatuses = (statuses: any) => {

    Object.keys(statuses).forEach(k => {
      statuses[k] = statuses[k].toLowerCase();
    });

    let status = statuses[Constants.menu.servicesMenuItems.credit];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.credit, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.voiVoe];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.voiVoe, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.voie];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.voie, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.voa];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.voa, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.aus];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.aus, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.appraisal];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.appraisal, status ?? MenuItemStatus.None);
    this._menuService.setStatus(Constants.menu.servicesMenuItems.appraisal + "-management", status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.disclosure];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.disclosure, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.pricing];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.pricing, status ?? MenuItemStatus.None);

    status = statuses[Constants.menu.servicesMenuItems.submission];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.submission, status ?? MenuItemStatus.None);
    this.loanSubmissionCompleted = (status == MenuItemStatus.Success);

    status = statuses[Constants.menu.servicesMenuItems.fees];
    this._menuService.setStatus(Constants.menu.servicesMenuItems.fees, status ?? MenuItemStatus.None);

    Object.keys(Constants.menu.servicesMenuItems).forEach(k => {
      const trackerStep = this.trackerSteps.find(step => step.id === k);
      const status = statuses[k] ?? MenuItemStatus.None
      if (trackerStep) {
        trackerStep.status = status;
      }
    });
  }

  private populateInitialPizzaTrackerStatuses = (items?: { [menuItemKey: string]: MenuItemStatus }) => {

    const item = items ? items[Constants.menu.servicesMenuItems.submission] : null;

    // populate first tracker
    const hasSubmission = item && item == MenuItemStatus.Success;
    if (!hasSubmission) {

      let steps = [];
      // For urla, we need to look at stepStatuses
      let urlaStatus = MenuItemStatus.Pending;
      if (this.stepStatuses.urla) {
        urlaStatus = this.stepStatuses.urla;
      }

      if (!this.disabledTpoAppMenuItems?.includes('Urla')) {
        steps.push({ id: 'urla', name: this._isCompanySmartFi ? "APPLICATION" : "URLA", order: (!this.isReverseMortgage ? 1 : 2), status: urlaStatus, navigateToPath: `/tpo/app-details/${this.loanId}/urla`, hiddenIcon: this.isUrlaReadOnly });
      }

      if (items[Constants.menu.servicesMenuItems.credit] && this.permissions.isCreditAllowed && this.loanSummary.channel != "Correspondent" && !this.isReverseMortgage && !this.disabledTpoAppMenuItems?.includes('Credit')) {
        steps.push({ id: Constants.menu.servicesMenuItems.credit, name: "CREDIT", order: 2, status: items[Constants.menu.servicesMenuItems.credit] ?? MenuItemStatus.None, navigateToPath: `/tpo/app-details/${this.loanId}/credit-reports` })
      }

      if (items[Constants.menu.servicesMenuItems.aus] && this.permissions.isAusAllowed && this.loanSummary.channel != "Correspondent" && !this.disabledTpoAppMenuItems?.includes('Aus')) {
        steps.push({ id: Constants.menu.servicesMenuItems.aus, name: "AUS", order: 3, status: items[Constants.menu.servicesMenuItems.aus] ?? MenuItemStatus.None, navigateToPath: `/tpo/app-details/${this.loanId}/aus` })
      }

      if (items[Constants.menu.servicesMenuItems.pricing] && this.permissions.pricingEnabled && !this.disabledTpoAppMenuItems?.includes('Pricing')) {
        steps.push({ id: Constants.menu.servicesMenuItems.pricing, name: "PRICING", order: (!this.isReverseMortgage ? 4 : 1), status: items[Constants.menu.servicesMenuItems.pricing] ?? MenuItemStatus.None, navigateToPath: `/tpo/app-details/${this.loanId}/pricing` })
      }

      if (!this.isDisableFees && items[Constants.menu.servicesMenuItems.fees] && this.permissions.isFeesEnabled && this.loanSummary.channel != "Correspondent" && !this.disabledTpoAppMenuItems?.includes('Fees')) {
        steps.push({ id: Constants.menu.servicesMenuItems.fees, name: "FEES", order: 5, status: items[Constants.menu.servicesMenuItems.fees] ?? MenuItemStatus.None, navigateToPath: `/tpo/app-details/${this.loanId}/fees` })
      }

      const isDisclosuresVisible = this.disclosuresEnabled && (this.permissions?.isDisclosureGenerationAllowed ||
        this.isBrokerDisclosedAndSubmitted) &&
        this.loanSummary.channel != 'Correspondent' &&
        !(
          this.isPRMG &&
          this.loanSummary.channel === 'NonDelegatedCorrespondent' &&
          this.applicationContext.application.nonDelOptions !==
          'AllDisclosuresThroughLender'
        ) && !this.disabledTpoAppMenuItems?.includes('Disclosures');

      if (items[Constants.menu.servicesMenuItems.disclosure] && isDisclosuresVisible && !this.disabledTpoAppMenuItems?.includes('Disclosures')) {
        steps.push({ id: Constants.menu.servicesMenuItems.disclosure, name: "DISCLOSURES", order: 6, status: items[Constants.menu.servicesMenuItems.disclosure] ?? MenuItemStatus.None, navigateToPath: `/tpo/app-details/${this.loanId}/disclosures` })
      }

      if (items[Constants.menu.servicesMenuItems.submission] && !this.disabledTpoAppMenuItems?.includes('Submission')) {
        steps.push({ id: Constants.menu.servicesMenuItems.submission, name: "SUBMIT TO UW", order: 7, status: items[Constants.menu.servicesMenuItems.submission] ?? MenuItemStatus.None, navigateToPath: `/tpo/app-details/${this.loanId}/submission` })
      }

      steps = steps.sort((a, b) => a.order - b.order);
      this.trackerSteps = [...steps] as PizzaTrackerStep[];
    } else {
      this.populateOtherPizzaTrackerSteps(this.loanStatus);
    }
  };

  private populateOtherPizzaTrackerSteps = (loanStatus: LoanStatus) => {
    if (!loanStatus || !loanStatus.loanStage || !this.loanId) {
      return;
    }

    var statuses = [];

    if ([StandardLoanStage.Application, StandardLoanStage.Origination, StandardLoanStage.Setup, StandardLoanStage.Processing, StandardLoanStage.Underwriting, StandardLoanStage.Approval].indexOf(loanStatus.loanStage) > -1) {
      statuses = this.applicationContext.globalConfig.loanStatus.filter(ls => ls.enabledChannels?.indexOf(this.application.channel) > -1 && [StandardLoanStage.Application, StandardLoanStage.Origination, StandardLoanStage.Setup, StandardLoanStage.Processing, StandardLoanStage.Underwriting, StandardLoanStage.Approval].indexOf(ls.loanStage) > -1);
    } else if ([StandardLoanStage.ClearToClose, StandardLoanStage.Completed].indexOf(loanStatus.loanStage) > -1) {
      statuses = this.applicationContext.globalConfig.loanStatus.filter(ls => ls.enabledChannels?.indexOf(this.application.channel) > -1 && [StandardLoanStage.ClearToClose, StandardLoanStage.Completed].indexOf(ls.loanStage) > -1);
    } else {
      return;
    }

    this._loanService.getPizzaMenuStatuses(this.loanId).subscribe({
      next: (menuStatuses) => {
        const prefix = 'LS_';
        const usedDisplayNames = new Set<string>();
        const validSteps: PizzaTrackerStep[] = [];

        statuses.forEach((status) => {
          const displayName = status.tpoStatusAlias?.trim() || status.loanStatusName;

          if (usedDisplayNames.has(displayName)) {
            return;
          }

          usedDisplayNames.add(displayName);

          validSteps.push({
            name: displayName,
            order: validSteps.length + 1,
            status: menuStatuses[prefix + status.loanStatusId]
          } as PizzaTrackerStep);
        });

        this.trackerSteps = validSteps;
      }
    });

  }

  private setActiveTrackerStepOrder = () => {
    const currentURL = this._router.url;

    if (currentURL.includes("/urla")) {
      this.activeTrackerStepOrder = 1;
    }
    else if (currentURL.includes("/credit-reports")) {
      this.activeTrackerStepOrder = 2;
    }
    else if (currentURL.includes("/aus")) {
      this.activeTrackerStepOrder = 3;
    }
    else if (currentURL.includes("pricing") || currentURL.includes("amortization")) {
      this.activeTrackerStepOrder = 4;
    }
    else if (currentURL.includes("/fees")) {
      this.activeTrackerStepOrder = 5;
    }
    else if (currentURL.includes("/disclosures")) {
      this.activeTrackerStepOrder = 6;
    }
    else if (currentURL.includes("/submission")) {
      this.activeTrackerStepOrder = 7;
    }
  }

  private getPermissions = () => {
    this._loanServicesService.getTpoConfiguration().subscribe((result: TpoConfiguration) => {
      this._tpoConfig = result;
      const isIncomeAnalysisEnabledForLoan = this.isIncomeAnalysisEnabledForChannel(result.incomeAnalysisEnabledChannels, this.application?.channel);
      this.permissions.isServicesEnabled = result.creditPermissions !== TpoCreditPermissions.None || result.ausPermissions !== TpoCreditPermissions.None || result.isVOAAllowed || result.isVOIVOEAllowed || result.linkIntegrationEnabled || result.linkIncomeCalculatorEnabled || isIncomeAnalysisEnabledForLoan;
      this.permissions.isCreditAllowed = result.creditPermissions !== TpoCreditPermissions.None;
      this.permissions.isVoiVoeAllowed = result.isVOIVOEAllowed;
      this.permissions.isVoaAllowed = result.isVOAAllowed;
      this.permissions.isAusAllowed = result.ausPermissions !== TpoCreditPermissions.None;
      this.permissions.isAppraisalOrderAllowed = result.isAppraisalOrderAllowed;
      this.permissions.columnSelectorEnabled = result.columnSelectorEnabled;
      this.permissions.isLinkEnabled = result.linkIntegrationEnabled;
      this.permissions.isLinkCalculatorEnabled = result.linkIncomeCalculatorEnabled;
      this.permissions.isIncomeAnalysisEnabled = isIncomeAnalysisEnabledForLoan;
      this.showExplanationSectionForMenuStatusIcons = result.showExplanationSectionForMenuStatusIcons;
    });
  }

  private isIncomeAnalysisEnabledForChannel(enabledChannels: string | null | undefined, channel: string | null | undefined): boolean {
    if (!enabledChannels || !channel) {
      return false;
    }
    return enabledChannels.split(',').map(c => c.trim()).includes(channel);
  }

  private getHiddenFields = (loanHiddenFields?: Configuration): Array<string> => {
    if (!loanHiddenFields?.valueStr) return [];
    return loanHiddenFields.valueStr?.split(",").map(el => el.trim());
  }

  private checkLoanPurpose = () => {
    if (!this.mortgage?.subjectProperty || !this.application.loanPurposeId) return;

    const loanPurposeName = this._loanPurposes.find(lp => lp.loanPurposeId === this.application.loanPurposeId)?.mortgageLoanPurpose;
    if (loanPurposeName && loanPurposeName !== this.mortgage.subjectProperty.purposeOfLoan) {
      const subPurpose = this.mortgage.subjectProperty.purposeOfLoan
        ? ` shows '${this.mortgage.subjectProperty.purposeOfLoan}'`
        : ' is missing';
      Swal.fire({
        title: `The loan purpose on the URLA ${subPurpose}, while the application loan purpose is '${loanPurposeName}'.`,
        text: `Would you like to update the URLA to '${loanPurposeName}' to match?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        reverseButtons: true
      }).then((result: SweetAlertResult) => {
        if (!result.value) {
          return;
        }
        this.mortgage.subjectProperty.purposeOfLoan = loanPurposeName;
        this._mortgageService.loanPurpose =
          loanPurposeName as LoanPurposeTypeEnum;
      });
    }
  }
}

export class TpoFeaturePermissions {
  pricingEnabled: boolean;
  isFeesEnabled: boolean;
  isCreditAllowed: boolean;
  isVoiVoeAllowed: boolean;
  isVoaAllowed: boolean;
  isAusAllowed: boolean;
  isAppraisalOrderAllowed: boolean;
  isServicesEnabled: boolean;
  columnSelectorEnabled: boolean;
  isAmortizationTableMenuItemVisible: boolean;
  isMersEnabled: boolean;
  isRequiredLoanAndCaseNumbers: boolean;
  isAppraisedValueHidden: boolean;
  isSubjectPropertyAddressHidden: boolean;
  isDisclosureGenerationAllowed: boolean;
  isLinkEnabled: boolean;
  isLinkCalculatorEnabled: boolean;
  isIncomeAnalysisEnabled: boolean;
}
